// One sales ticket: its current lines with VAT code and VAT in cents, its payments and its
// revisions (the source edited it; earlier lines are kept, never overwritten).

import type { SaleTicketDetail, SaleTicketLine } from '@enp/domain/api';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  Money,
  PageHeader,
  Qty,
  Skeleton,
  StatTile,
  type ColumnDef,
} from '@enp/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  notFound,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { ArrowLeft, ReceiptText, TriangleAlert, Undo2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { ApiError } from '../../../lib/api.js';
import { TicketBadges, ticketLabel } from '../../../sales/components.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { StockStateBadge } from '../../../stock/components.js';
import {
  methodLabel,
  ORDER_TYPE_LABEL,
  plainDateLabel,
  accountToday,
  VAT_CODE_LABEL,
} from '../../../stock/labels.js';
import { ticketQuery } from '../../../stock/queries.js';
import { AccountDate } from '../../../shell/account-date.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/sales/$ticketId')({
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.ticketId)) throw notFound();
    try {
      await context.queryClient.ensureQueryData(ticketQuery(params.ticketId));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: TicketPage,
  pendingComponent: TicketSkeleton,
  errorComponent: TicketError,
  notFoundComponent: TicketNotFound,
});

function BackToSales({ label }: { label: string }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/sales">Sales</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{label}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function Panel({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card role="region" aria-labelledby={`${id}-title`}>
      <CardHeader>
        <CardTitle as="h2" id={`${id}-title`}>
          {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

const lineCol = createColumnHelper<SaleTicketLine>();

// TanStack's documented DataTable typing: columns carry heterogeneous value types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lineColumns: ColumnDef<SaleTicketLine, any>[] = [
  lineCol.display({
    id: 'item',
    header: 'Item',
    meta: { width: 'min-w-56' },
    cell: ({ row }) => {
      const l = row.original;
      return (
        <span className="flex flex-col gap-0.5 leading-tight">
          {l.product ? (
            <Link
              to="/catalogue/$productId"
              params={{ productId: l.product.id }}
              className="font-medium text-fg hover:underline"
            >
              {l.product.name}
            </Link>
          ) : l.line_kind === 'delivery' ? (
            <span className="font-medium text-fg">Delivery fee</span>
          ) : (
            <span className="flex items-center gap-1.5 font-medium text-state-warning">
              <TriangleAlert aria-hidden className="size-4" />
              Not matched to a product
            </span>
          )}
          <span className="text-xs text-muted">
            {l.description ?? 'No description'}
            {(l.external_variant_id ?? l.external_item_id) && (
              <>
                {' · '}
                <span className="font-mono">{l.external_variant_id ?? l.external_item_id}</span>
              </>
            )}
          </span>
        </span>
      );
    },
  }),
  lineCol.display({
    id: 'state',
    header: 'Sold as',
    meta: { className: 'hidden lg:table-cell' },
    cell: ({ row }) =>
      row.original.state ? (
        <StockStateBadge state={row.original.state} />
      ) : (
        <span className="text-muted">
          <span aria-hidden>—</span>
          <span className="sr-only">No particular state</span>
        </span>
      ),
  }),
  lineCol.display({
    id: 'qty',
    header: 'Qty',
    meta: { align: 'right' },
    cell: ({ row }) => <Qty value={row.original.qty} unit={row.original.product?.base_uom} />,
  }),
  lineCol.display({
    id: 'price',
    header: 'Unit price',
    meta: { align: 'right', className: 'hidden xl:table-cell' },
    cell: ({ row }) => <Money cents={row.original.unit_price_cents} />,
  }),
  lineCol.display({
    id: 'discount',
    header: 'Discount',
    meta: { align: 'right' },
    cell: ({ row }) =>
      row.original.discount_cents === 0 ? (
        <span className="text-muted">—</span>
      ) : (
        <Money cents={-row.original.discount_cents} />
      ),
  }),
  lineCol.display({
    id: 'total',
    header: 'Total',
    meta: { align: 'right' },
    cell: ({ row }) => <Money cents={row.original.total_cents} className="font-medium" />,
  }),
  lineCol.display({
    id: 'vat_code',
    header: 'VAT code',
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-sm">
        {VAT_CODE_LABEL[row.original.vat_code] ?? row.original.vat_code}
      </span>
    ),
  }),
  lineCol.display({
    id: 'vat',
    header: 'VAT',
    meta: { align: 'right' },
    cell: ({ row }) => <Money cents={row.original.vat_cents} />,
  }),
];

function Payments({ ticket }: { ticket: SaleTicketDetail }) {
  const paid = ticket.payments.reduce((sum, p) => sum + p.amount_cents, 0);
  return (
    <Panel
      id="payments"
      title="Payments"
      description={
        ticket.payments.length === 0
          ? undefined
          : paid === ticket.total_cents
            ? ticket.ticket_type === 'refund'
              ? 'Refunded in full.'
              : 'Paid in full.'
            : `Payments add up to a different amount from the total.`
      }
    >
      {ticket.payments.length === 0 ? (
        <p className="text-body text-muted">No payments recorded on this ticket.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {ticket.payments.map((p) => (
            <li key={p.id} className="flex items-baseline justify-between gap-3 py-2">
              <span className="flex flex-col leading-tight">
                <span className="font-medium text-fg">{methodLabel(p.method)}</span>
                <span className="text-xs text-muted">
                  <AccountDate value={p.paid_at} withTime />
                  {p.external_ref && (
                    <>
                      {' · '}
                      <span className="font-mono">{p.external_ref}</span>
                    </>
                  )}
                </span>
              </span>
              {/* A refund is not a problem: the minus carries it, in the normal colour (UI8A-11). */}
              <Money cents={p.amount_cents} className="font-medium" />
            </li>
          ))}
          <li className="flex items-baseline justify-between gap-3 py-2 font-semibold">
            <span>{ticket.ticket_type === 'refund' ? 'Refunded' : 'Paid'}</span>
            <Money cents={paid} />
          </li>
        </ul>
      )}
    </Panel>
  );
}

function Revisions({ ticket }: { ticket: SaleTicketDetail }) {
  return (
    <Panel
      id="revisions"
      title="Revisions"
      description={
        ticket.revisions.length <= 1
          ? 'Never edited at the source.'
          : `Edited ${ticket.revisions.length - 1} ${ticket.revisions.length === 2 ? 'time' : 'times'} at the source. Earlier lines are kept; the newest revision counts.`
      }
    >
      <ol className="flex flex-col divide-y divide-border">
        {ticket.revisions.map((r) => {
          const current = r.revision === ticket.revision;
          return (
            <li key={r.revision} className="flex items-baseline justify-between gap-3 py-2">
              <span className="flex flex-col leading-tight">
                <span className="font-medium text-fg">
                  {r.revision === 0 ? 'Original' : `Revision ${r.revision}`}
                  {current && <span className="text-sm font-normal text-muted"> (current)</span>}
                </span>
                <span className="text-xs text-muted">
                  {r.line_count} {r.line_count === 1 ? 'line' : 'lines'} · VAT{' '}
                  <Money cents={r.vat_cents} />
                </span>
              </span>
              <Money cents={r.total_cents} className={current ? 'font-medium' : 'text-muted'} />
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

function TicketPage() {
  const { ticketId } = Route.useParams();
  const { data: t } = useSuspenseQuery(ticketQuery(ticketId));
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const label = ticketLabel(t);
  useDocumentTitle(`Receipt ${label}`);

  return (
    <>
      <PageHeader
        eyebrow={<BackToSales label={`Receipt ${label}`} />}
        // One layout for every ticket (review UI2A-218): its kind and state beside the
        // number, then where and when, then what it refunds or that it was cancelled.
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span>Receipt {label}</span>
            <span className="inline-flex flex-wrap items-center gap-1.5 font-body text-base font-normal tracking-normal">
              <TicketBadges ticket={t} />
            </span>
          </span>
        }
        description={
          <span className="flex flex-col gap-1">
            <span>
              {t.channel.name} · <AccountDate value={t.occurred_at} withTime /> · trading day{' '}
              {plainDateLabel(t.trading_day, today)} ·{' '}
              {ORDER_TYPE_LABEL[t.order_type] ?? t.order_type}
            </span>
            {t.refund_of_ticket_id && (
              <span className="flex items-center gap-2 text-fg">
                <Undo2 aria-hidden className="size-4 text-state-warning" />
                <span>
                  Refunds{' '}
                  <Link
                    to="/sales/$ticketId"
                    params={{ ticketId: t.refund_of_ticket_id }}
                    className="font-medium text-primary-text underline underline-offset-2"
                  >
                    {t.refund_of ? `receipt ${ticketLabel(t.refund_of)}` : 'the original sale'}
                  </Link>
                </span>
              </span>
            )}
            {t.status === 'cancelled' && (
              <span className="text-fg">
                Cancelled at the source: its stock went back, and it doesn’t count towards sales.
              </span>
            )}
          </span>
        }
      />
      <section
        aria-label="Totals"
        className={cn(
          'mb-6 grid gap-4 sm:grid-cols-2',
          t.discount_cents !== 0 ? 'xl:grid-cols-4' : 'xl:grid-cols-3',
        )}
      >
        <StatTile label="Total" value={<Money cents={t.total_cents} />} hint="VAT included" />
        <StatTile label="VAT" value={<Money cents={t.vat_cents} />} hint="Sum of the lines’ VAT" />
        {t.discount_cents !== 0 && (
          // No "Discounts R 0.00" tile on a ticket without any (UI2A-218).
          <StatTile
            label="Discounts"
            value={<Money cents={t.discount_cents} />}
            hint={
              <>
                Before discounts <Money cents={t.gross_cents} />
              </>
            }
          />
        )}
        <StatTile
          label="Lines"
          value={t.line_count}
          tone={t.unmapped_line_count > 0 ? 'warning' : 'neutral'}
          hint={
            t.unmapped_line_count > 0
              ? `${t.unmapped_line_count} ${t.unmapped_line_count === 1 ? 'line' : 'lines'} not in the catalogue`
              : 'All in the catalogue'
          }
        />
      </section>
      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_20rem]">
        <section aria-labelledby="lines-title" className="flex min-w-0 flex-col gap-3">
          <h2 id="lines-title" className="font-display text-lg font-semibold text-fg">
            Lines
            {t.revision > 0 && (
              <span className="font-body text-sm font-normal text-muted">
                {' '}
                · revision {t.revision}
              </span>
            )}
          </h2>
          <DataTable
            label="Ticket lines"
            columns={lineColumns}
            data={t.lines}
            filterable={false}
            getRowId={(l) => l.id}
            empty={
              <EmptyState
                compact
                icon={ReceiptText}
                title="No lines"
                description="The source sent this ticket without any lines."
              />
            }
            footer={
              t.lines.length > 0 ? (
                <p className="text-sm text-muted">
                  VAT is worked out per line and rounded to the cent; the ticket’s VAT is their sum.
                </p>
              ) : undefined
            }
          />
        </section>
        <div className="grid min-w-0 content-start gap-6 lg:grid-cols-2 2xl:grid-cols-1">
          <Payments ticket={t} />
          <Revisions ticket={t} />
        </div>
      </div>
    </>
  );
}

function TicketSkeleton() {
  return (
    <div role="status" aria-live="polite">
      <h1 className="sr-only">Loading receipt</h1>
      <span className="sr-only">Loading receipt…</span>
      <div className="flex flex-col gap-2 pb-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-60" />
        <Skeleton className="h-5 w-96 max-w-full" />
      </div>
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-28 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-72 w-full rounded-lg" />
    </div>
  );
}

function BackButton() {
  return (
    <Button variant="secondary" asChild>
      <Link to="/sales">
        <ArrowLeft aria-hidden />
        Back to sales
      </Link>
    </Button>
  );
}

function TicketError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <div className="max-w-2xl">
      <ErrorPanel
        error={error}
        what="This receipt"
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
        extraAction={<BackButton />}
      />
    </div>
  );
}

function TicketNotFound() {
  useDocumentTitle('Receipt not found');
  // Full width, with the way back above it, like the page it stands in for (UI2A-19).
  return (
    <>
      <div className="pb-4">
        <BackToSales label="Not found" />
      </div>
      <EmptyState
        icon={ReceiptText}
        titleAs="h1"
        title="Receipt not found"
        description="There’s no sales ticket at this address. It may be in another account, or the link may be mistyped."
        action={<BackButton />}
      />
    </>
  );
}
